import React from "react";
import { CircularProgress } from "@mui/material";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import {
  DeletePopUp,
  ErrorPanel,
  ErrorPanelHeader,
} from "../../../../../components/PopUp";
import { pluralize } from "../../../../../utils";
import { DeleteInvestmentModalProps, ImpactRow } from "./types";
import {
  ImpactList,
  ImpactItem,
  ImpactIcon,
  ImpactCount,
  SuccessPanel,
  LoadingPanel,
  FootNote,
} from "./styles";

export const DeleteInvestmentModal: React.FC<DeleteInvestmentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  investmentName,
  deletionImpact,
  isLoadingImpact = false,
  isDeleting = false,
  deleteError,
}) => {
  const buildImpactRows = (): ImpactRow[] => {
    if (!deletionImpact) {
      return [];
    }

    const {
      totalTransactions,
      expenseCount,
      incomeCount,
      transferCount,
      loanPaymentCount,
      recurringTransactionsCount,
    } = deletionImpact;

    // Market-update transactions are counted in totalTransactions but have no
    // dedicated field; derive them as the uncategorized remainder so the rows
    // reconcile with the heading total.
    const categorizedCount =
      expenseCount + incomeCount + transferCount + loanPaymentCount;
    const marketUpdateCount = Math.max(totalTransactions - categorizedCount, 0);

    const rows: ImpactRow[] = [
      {
        tone: "expense",
        icon: <TrendingDownIcon />,
        count: expenseCount,
        label: "expense",
      },
      {
        tone: "income",
        icon: <TrendingUpIcon />,
        count: incomeCount,
        label: "income transaction",
      },
      {
        tone: "transfer",
        icon: <SwapHorizIcon />,
        count: transferCount,
        label: "transfer",
      },
      {
        tone: "market",
        icon: <ShowChartRoundedIcon />,
        count: marketUpdateCount,
        label: "market update",
      },
      {
        tone: "loan",
        icon: <CreditCardIcon />,
        count: loanPaymentCount,
        label: "loan payment",
      },
      {
        tone: "recurring",
        icon: <AutorenewIcon />,
        count: recurringTransactionsCount,
        label: "recurring transaction",
      },
    ];

    return rows.filter((row) => row.count > 0);
  };

  const renderImpactDetails = () => {
    if (isLoadingImpact) {
      return (
        <LoadingPanel
          role="status"
          aria-label="Loading deletion impact information"
        >
          <CircularProgress size={16} aria-label="Loading" />
          Loading deletion impact…
        </LoadingPanel>
      );
    }

    if (!deletionImpact) {
      return null;
    }

    const { totalTransactions, recurringTransactionsCount } = deletionImpact;
    const affectedCount = totalTransactions + recurringTransactionsCount;

    if (affectedCount === 0) {
      return (
        <SuccessPanel role="status" aria-label="No transactions affected">
          <CheckCircleRoundedIcon aria-hidden="true" />
          No transactions will be affected by this deletion.
        </SuccessPanel>
      );
    }

    const impactRows = buildImpactRows();
    const headingText = `${affectedCount.toLocaleString()} ${pluralize(affectedCount, "transaction")} will be permanently impacted or deleted:`;

    return (
      <ErrorPanel
        role="alert"
        aria-label={`Warning: ${affectedCount} transactions will be affected`}
      >
        <ErrorPanelHeader>
          <WarningAmberRoundedIcon aria-hidden="true" />
          {headingText}
        </ErrorPanelHeader>
        <ImpactList aria-label="Transaction impact breakdown">
          {impactRows.map((row) => (
            <ImpactItem key={row.tone}>
              <ImpactIcon $tone={row.tone} aria-hidden="true">
                {row.icon}
              </ImpactIcon>
              <ImpactCount>{row.count.toLocaleString()}</ImpactCount>
              {pluralize(row.count, row.label)}
            </ImpactItem>
          ))}
        </ImpactList>
      </ErrorPanel>
    );
  };

  return (
    <DeletePopUp
      isOpen={isOpen}
      setIsOpen={onClose}
      title="Delete investment"
      text={
        <>
          Do you really want to delete the <strong>{investmentName}</strong>{" "}
          investment account?
          {renderImpactDetails()}
          <FootNote>
            <strong>This action cannot be undone.</strong>
          </FootNote>
          <FootNote>
            All related transactions are kept but will no longer be associated
            with a valid account. If you want to preserve the history, consider
            deactivating the investment instead.
          </FootNote>
        </>
      }
      handleOnDelete={onConfirm}
      isLoading={isDeleting}
      errorMessage={deleteError}
    />
  );
};
